import {
  faLinkedin,
  faSquareFacebook,
  faSquareInstagram,
  faTwitter,
  faXing,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Skeleton } from "@mui/material";
import { Mail, User } from "lucide-react";
import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  ContentContainer,
  EmailLink,
  ImageContainerAnsprechpartner,
  Name,
  Role,
  SocialMediaIcons,
  SocialMediaLink,
  StyledCard,
} from "../components/StyledComponents";
import useFetchPaginatedElements from "../fetchFunction/OverviewFetch";
import "../styles/Ansprechpartner.css";

const socialMediaIcons = {
  twitter: faTwitter,
  facebook: faSquareFacebook,
  linkedin: faLinkedin,
  instagram: faSquareInstagram,
  youtube: faYoutube,
  xing: faXing,
};

const validateAnsprechpartner = (data) => {
  if (!data || !Array.isArray(data)) {
    console.warn("Invalid or missing data:", data);

    return [];
  }

  const salutationMap = {
    f: "Frau",
    m: "Herr",
    u: "",
  };

  return data.map((item) => {
    const salutation =
      salutationMap[item.salutation?.toLowerCase()] || "Unbekannt";
    const contactperson = `${salutation} ${item.first_name || ""} ${
      item.last_name || ""
    }`.trim();

    return {
      id: item.id || null,
      contactperson: contactperson !== "Unbekannt" ? contactperson : "",
      role: item.position || "No Position Specified",
      imageUrl: item.image_url || null,
      email: item.email || null,
      department: item.department || "No Department",
      socialMedia: item.socialMedia || {},
    };
  });
};

const extractUserName = (url) => {
  if (!url) return null;
  const urlParts = url.split("/");

  return urlParts[urlParts.length - 1];
};

const AnsprechpartnerOverview = ({ styling, companyAccessname }) => {
  const [currentPage] = useState(1);
  const pageSize = 10;

  const {
    loading,
    error,
    ansprechpartner: ansprechpartnerData,
  } = useFetchPaginatedElements(
    companyAccessname,
    "ansprechpartner",
    currentPage,
    pageSize
  );

  const validAnsprechpartner = validateAnsprechpartner(ansprechpartnerData);

  const renderSocialMediaIcons = (socialMediaLinks) => (
    <SocialMediaIcons>
      {Object.entries(socialMediaLinks).map(([platform, url]) => {
        const icon = socialMediaIcons[platform] || socialMediaIcons.default;

        return (
          <SocialMediaLink
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: styling.linkColor }}
          >
            <FontAwesomeIcon
              icon={icon}
              style={{ marginRight: "6px", color: styling.iconColor }}
            />
            {extractUserName(url)}
          </SocialMediaLink>
        );
      })}
    </SocialMediaIcons>
  );

  if (loading) {
    return (
      <div>
        {Array.from(new Array(pageSize)).map((_, index) => (
          <StyledCard key={index}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={160}
              style={{ borderRadius: "10px" }}
            />
            <div style={{ flex: 1, marginLeft: "20px" }}>
              <Skeleton variant="text" width="60%" height={30} />
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton
                variant="rectangular"
                width="50%"
                height={20}
                style={{ marginTop: "10px" }}
              />
            </div>
          </StyledCard>
        ))}
      </div>
    );
  }

  if (error) {
    console.error("Error fetching data:", error);

    return <p>Error: {error.message || "An error occurred."}</p>;
  }

  if (validAnsprechpartner.length === 0) {
    console.warn("No valid contacts found.");

    return <p>No contacts available.</p>;
  }

  return (
    <div>
      {validAnsprechpartner.map((ansprechpartner) => (
        <StyledCard key={ansprechpartner.id}>
          <ImageContainerAnsprechpartner>
            {ansprechpartner.imageUrl ? (
              <img
                src={ansprechpartner.imageUrl}
                alt={ansprechpartner.contactperson}
              />
            ) : (
              <div className="placeholder-frame">
                <User size={48} className="placeholder-icon" />
              </div>
            )}
          </ImageContainerAnsprechpartner>
          <ContentContainer>
            <Name>{ansprechpartner.contactperson}</Name>
            <Role>{ansprechpartner.role}</Role>
            <p>{ansprechpartner.department}</p>
            {ansprechpartner.email && (
              <EmailLink
                href={`mailto:${ansprechpartner.email}`}
                style={{ color: styling.linkColor }}
              >
                <Mail
                  size={16}
                  className="icon"
                  style={{ color: styling.iconColor }}
                />{" "}
                {ansprechpartner.email}
              </EmailLink>
            )}
            {renderSocialMediaIcons(ansprechpartner.socialMedia)}
          </ContentContainer>
        </StyledCard>
      ))}
    </div>
  );
};

AnsprechpartnerOverview.propTypes = {
  styling: PropTypes.shape({
    linkColor: PropTypes.string,
    iconColor: PropTypes.string,
  }),
  companyAccessname: PropTypes.string.isRequired,
};

export default AnsprechpartnerOverview;
